(function () {
  'use strict';
  if (customElements.get('js-diesel-finder')) return;
  const core = window.JSDieselFitment;
  if (!core) return;
  const store = new Map();
  const TTL = 60000;
  const complete = values => ['make', 'year', 'model', 'engine'].every(key => !!values[key]);
  const blanks = { make: 'Select make', model: 'Select model', year: 'Select year', engine: 'Select engine size', type: 'Any / not sure', body: 'All body styles' };
  const emptyValues = () => Object.fromEntries(core.keys.map(key => [key, '']));
  async function payload(url, selector, signal) {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('Vehicle data request failed');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const node = doc.querySelector(selector);
    if (!node) throw new Error('Vehicle data is unavailable');
    return JSON.parse(node.textContent);
  }
  async function loadApplications(collectionURL, handle, optionsOnly = false) {
    const base = core.urlFor(collectionURL, location.href, {}, 'catalogue');
    const key = base.href + (optionsOnly ? '#vehicle-options' : '#product-results');
    const previous = store.get(key);
    if (previous && previous.expires > Date.now()) return previous.promise;
    const entry = { expires: Date.now() + TTL };
    entry.promise = (async () => {
      const products = [];
      const ids = new Set();
      let pages = null;
      let total = null;
      async function fetchPage(page) {
        const url = new URL(base);
        url.searchParams.set('section_id', optionsOnly ? 'js-diesel-vehicle-options' : 'js-diesel-fitment-data');
        url.searchParams.set('page', String(page));
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 15000);
        try { return await payload(url, '[data-jsdf-applications]', controller.signal); }
        finally { clearTimeout(timer); }
      }
      function accept(data, page) {
        if (data.version !== 1 || data.collection !== handle || !Array.isArray(data.products) || data.page !== page || !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 50 || (!Number.isInteger(data.total) || data.total < 0 || data.total > 500)) throw new Error('Incomplete vehicle catalogue');
        if (total !== null && (total !== data.total || pages !== data.pages)) throw new Error('Catalogue changed during loading. Please retry.');
        pages = data.pages;
        total = data.total;
        for (const product of data.products) {
          if (!product.id || ids.has(String(product.id)) || !Array.isArray(product.variants) || product.variantCount !== product.variants.length || product.variants.some(v => !Array.isArray(v.applications) || v.applicationCount !== v.applications.length)) throw new Error('Incomplete product data');
          ids.add(String(product.id));
          products.push(product);
        }
      }
      accept(await fetchPage(1), 1);
      // Fetch small batches concurrently instead of waiting for each page in turn.
      for (let first = 2; first <= pages; first += 3) {
        const numbers = Array.from({ length: Math.min(3, pages - first + 1) }, (_, i) => first + i);
        const batch = await Promise.all(numbers.map(fetchPage));
        batch.forEach((data, index) => accept(data, numbers[index]));
      }
      if (products.length !== total) throw new Error('Incomplete vehicle catalogue');
      return { products, rows: core.index(products) };
    })().catch(error => { if (store.get(key) === entry) store.delete(key); throw error; });
    store.set(key, entry);
    return entry.promise;
  }
  class DieselFinder extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.mode = this.dataset.mode === 'applications' ? 'applications' : 'catalogue';
      this.selectionOnly = this.dataset.selectionOnly === 'true';
      this.autoApply = !this.selectionOnly && this.dataset.autoApply === 'true';
      this.progressive = !this.selectionOnly && this.dataset.progressive === 'true';
      this.fields = Object.fromEntries([...this.querySelectorAll('select[data-key]')].map(field => [field.dataset.key, field]));
      this.status = this.querySelector('.jsdf__status');
      this.submit = this.querySelector('[type="submit"]');
      this.retry = this.querySelector('.jsdf__retry');
      this.selection = core.readValues(new URL(location.href), this.mode);
      const remembered = window.JSSVehicle?.get();
      if (remembered) this.selection = { ...remembered.values };
      this.confirmedKeys = new Set(remembered && window.JSSVehicle.equal(this.selection, remembered.values) ? remembered.confirmedKeys : []);
      this.suppressed = new Set();
      this.cache = new Map();
      this.sequence = 0;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      window.addEventListener('jss:vehicle-change', event => {
        if (!this.saving) this.receiveVehicle(event.detail);
      }, options);
      const vehicleToggle = this.querySelector('[data-jsdf-vehicle-toggle]');
      const vehicleFields = this.querySelector('[data-jsdf-vehicle-fields]');
      if (vehicleToggle && vehicleFields) {
        vehicleToggle.hidden = false;
        // A selected vehicle starts compact so its matching products stay in view.
        // Keep the existing mobile default for first visits without a vehicle too.
        if (complete(this.selection) || window.matchMedia('(max-width: 767px)').matches) this.setVehicleFields(false, false);
        vehicleToggle.addEventListener('click', () => {
          this.setVehicleFields(vehicleToggle.getAttribute('aria-expanded') !== 'true', true);
        }, options);
      }
      const panel = this.querySelector('[data-jsdf-collapse]');
      if (panel) {
        const panelKey = 'jsdf-panel:' + this.dataset.panelKey + ':' + this.dataset.collectionHandle + ':' + (new URL(location.href).searchParams.get('preview_theme_id') || '');
        try {
          const saved = sessionStorage.getItem(panelKey);
          panel.open = new URL(location.href).searchParams.get('jsdf_open') === '1' || !complete(this.selection) || (!this.progressive && saved === '1');
        } catch (_) { panel.open = !complete(this.selection); }
        // Collection-page vehicle filters also start closed on mobile.
        if (!this.selectionOnly && window.matchMedia('(max-width: 767px)').matches) panel.open = false;
        panel.addEventListener('toggle', () => {
          if (this.progressive && this.dataset.resultsPage === 'true') {
            const results = document.querySelector('js-diesel-results');
            const applied = core.readValues(new URL(location.href), this.mode);
            if (results) results.hidden = panel.open || !complete(applied);
            if (!panel.open && complete(applied) && core.keys.some(key => (applied[key] || '') !== (this.selection[key] || ''))) {
              this.selection = applied;
              this.suppressed.clear();
              this.refresh(false);
            }
          }
          try { sessionStorage.setItem(panelKey, panel.open ? '1' : '0'); } catch (_) { /* Storage is optional for the disclosure. */ }
        }, options);
      }
      for (const [key, field] of Object.entries(this.fields)) {
        field.name = (this.mode === 'applications' ? core.params : core.catalogueParams)[key] || '';
        field.addEventListener('change', () => {
          const value = field.value;
          this.selection[key] = value;
          if (value) this.confirmedKeys.add(key); else this.confirmedKeys.delete(key);
          if (this.dataset.resultsPage === 'true') {
            const results = document.querySelector('js-diesel-results');
            if (results) results.hidden = true;
          }
          const after = core.keys.slice(core.keys.indexOf(key) + 1);
          after.forEach(k => { this.selection[k] = ''; this.suppressed.delete(k); this.confirmedKeys.delete(k); });
          if (!value) this.suppressed.add(key); else this.suppressed.delete(key);
          if (key === 'make') this.suppressed.clear();
          this.pendingCollectionChange = this.autoApply;
          this.refresh(true).then(result => this.applyCollectionChange(result));
        }, options);
      }
      this.querySelector('.jsdf__reset').addEventListener('click', () => {
        if (window.JSSVehicle) {
          window.JSSVehicle.clear();
          return;
        }
        this.selection = emptyValues();
        this.confirmedKeys.clear();
        if (!this.selectionOnly) window.JSSVehicle?.clear();
        if (!this.selectionOnly && this.mode === 'applications') {
          try { sessionStorage.removeItem('jsdf-vehicle-v2'); } catch (_) {}
          if (document.querySelector('js-diesel-results')) {
            this.navigate(core.urlFor(this.dataset.collectionUrl, location.href, {}, 'applications'));
            return;
          }
        }
        this.suppressed.clear();
        this.pendingCollectionChange = this.autoApply;
        this.refresh(false).then(result => {
          if (this.autoApply) this.applyCollectionChange(result);
          else if (this.isConnected) this.fields.make.focus();
        });
      }, options);
      this.retry.addEventListener('click', () => {
        this.cache.clear();
        this.refresh(!!this.pendingCollectionChange).then(result => {
          if (this.pendingCollectionChange) this.applyCollectionChange(result);
        });
      }, options);
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (!this.submit.disabled && this.form.reportValidity()) {
          this.confirmedKeys = new Set(core.keys.filter(k => this.selection[k]));
          this.remember();
          if (this.selectionOnly) {
            this.dispatchEvent(new CustomEvent('jss:vehicle-saved', { bubbles: true }));
            return;
          }
          this.navigate(core.urlFor(this.dataset.collectionUrl, location.href, this.selection, this.mode, this.autoApply));
        }
      }, options);
      if (!this.selectionOnly) this.refresh(false);
    }
    remember() {
      this.saving = true;
      try { window.JSSVehicle?.set(this.selection, [...this.confirmedKeys]); }
      finally { this.saving = false; }
    }
    receiveVehicle(record) {
      this.selection = record ? { ...record.values } : emptyValues();
      this.confirmedKeys = new Set(record?.confirmedKeys || []);
      this.suppressed.clear();
      this.pendingCollectionChange = false;
      if (!record) {
        const url = new URL(location.href);
        if (this.autoApply && Object.values(core.catalogueParams).some(name => url.searchParams.has(name))) {
          this.navigate(core.urlFor(this.dataset.collectionUrl, url, {}, 'catalogue', true));
          return;
        }
        const panel = this.querySelector('[data-jsdf-collapse]');
        if (panel) panel.open = true;
        const fields = this.querySelector('[data-jsdf-vehicle-fields]');
        const toggle = this.querySelector('[data-jsdf-vehicle-toggle]');
        this.setVehicleFields(true, false);
      }
      // Product dialogs fetch their options when opened.
      if (!this.selectionOnly || (this.closest('jss-vehicle-panel') && !this.closest('jss-vehicle-panel').hidden)) this.refresh(false);
    }
    setVehicleFields(open, animate = false) {
      const fields = this.querySelector('[data-jsdf-vehicle-fields]');
      const toggle = this.querySelector('[data-jsdf-vehicle-toggle]');
      if (!fields || !toggle) return;
      const start = fields.hidden ? 0 : fields.getBoundingClientRect().height;
      if (this.fieldsAnimation) { this.fieldsAnimation.onfinish = null; this.fieldsAnimation.cancel(); }
      this.fieldsAnimation = null;
      fields.style.height = '';
      fields.style.overflow = '';
      toggle.setAttribute('aria-expanded', String(open));
      toggle.querySelector('[data-jsdf-vehicle-symbol]').textContent = open ? '−' : '+';
      if (!animate || !fields.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        fields.hidden = !open;
        return;
      }
      fields.hidden = false;
      const end = open ? fields.getBoundingClientRect().height : 0;
      const padding = getComputedStyle(fields);
      const top = padding.paddingTop, bottom = padding.paddingBottom;
      fields.style.overflow = 'hidden';
      const animation = this.fieldsAnimation = fields.animate([
        { height: start + 'px', paddingTop: start ? top : '0px', paddingBottom: start ? bottom : '0px', opacity: start ? 1 : 0 },
        { height: end + 'px', paddingTop: open ? top : '0px', paddingBottom: open ? bottom : '0px', opacity: open ? 1 : 0 }
      ], { duration: 240, easing: 'ease-in-out' });
      animation.onfinish = () => {
        if (this.fieldsAnimation !== animation) return;
        fields.hidden = !open;
        fields.style.overflow = '';
        this.fieldsAnimation = null;
        animation.cancel();
      };
    }
    setVehicle(values, applications = []) {
      this.selection = window.JSSVehicle ? window.JSSVehicle.clean(values) : { ...emptyValues(), ...values };
      this.applicationHints = Array.isArray(applications) ? applications : [];
      this.suppressed.clear();
      const remembered = window.JSSVehicle?.get();
      this.confirmedKeys = new Set(remembered && window.JSSVehicle.equal(this.selection, remembered.values) ? remembered.confirmedKeys : []);
      return this.refresh(false);
    }
    navigate(url) {
      if (this.mode === 'applications') {
        if (core.vehicleKeys.every(k => this.selection[k] && this.confirmedKeys.has(k))) url.searchParams.set('jsdf_confirmed', '1');
        else url.searchParams.delete('jsdf_confirmed');
        const category = new URL(location.href).searchParams.get('jsdf_category');
        if (category) url.searchParams.set('jsdf_category', category);
        try { sessionStorage.setItem('jsdf-vehicle-v2', JSON.stringify(this.selection)); } catch (_) {}
      }
      location.assign(url.href);
    }
    canChoose(key) {
      const primary = ['make', 'year', 'model', 'engine'];
      const parents = primary.includes(key) ? primary.slice(0, primary.indexOf(key)) : primary;
      return parents.every(parent => !!this.selection[parent]);
    }
    applyCollectionChange(result) {
      if (!this.autoApply || !result || result.sequence !== this.sequence || !this.isConnected) return;
      if (this.mode === 'catalogue' && this.selection.make && this.selection.model && !core.catalogueModelAllowed(this.selection.make, this.selection.model)) return;
      if (this.mode === 'catalogue' && this.selection.make && this.selection.type && !core.catalogueTypeAllowed(this.selection.make, this.selection.type)) return;
      this.pendingCollectionChange = false;
      this.remember();
      this.message('Updating products…');
      this.navigate(core.urlFor(this.dataset.collectionUrl, location.href, this.selection, this.mode, true));
    }
    disconnectedCallback() {
      this.sequence++;
      this.events?.abort();
      this.request?.abort();
      this.events = null;
    }
    message(text, error = false) {
      this.status.textContent = text;
      this.status.classList.toggle('is-error', error);
      const guidance = !error && /show matching parts/i.test(text);
      this.status.classList.toggle('jsdf__status--guidance', guidance);
      if (guidance) this.status.insertAdjacentHTML('afterbegin', '<svg class="jsdf__status-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5"/><path d="M12 10.5v6M12 7.5v.1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>');
    }
    async catalogue(values, signal) {
      const url = core.urlFor(this.dataset.collectionUrl, location.href, values, 'catalogue', this.autoApply);
      url.searchParams.set('section_id', 'js-diesel-finder-data');
      const key = url.href;
      const cached = this.cache.get(key);
      if (cached && cached.expires > Date.now()) return cached.data;
      const data = await payload(url, '[data-jsdf-payload]', signal);
      if (data.collection !== this.dataset.collectionHandle || !Number.isFinite(data.count) || !Array.isArray(data.filters) || (data.count > 0 && !data.filters.some(f => f.name === core.catalogueParams.make))) throw new Error('Vehicle filters are unavailable');
      if (this.cache.size > 30) this.cache.clear();
      this.cache.set(key, { data, expires: Date.now() + TTL });
      return data;
    }
    async state(values, signal) {
      const facets = {};
      if (this.mode === 'applications') {
        const { rows } = await loadApplications(this.dataset.collectionUrl, this.dataset.collectionHandle, true);
        core.keys.forEach(key => { facets[key] = core.facets(rows, values, key); });
        // Progressive selection only needs match availability, not grouped product results.
        return { count: (this.progressive || this.selectionOnly) ? core.matchingRows(rows, values).length : core.results(rows, values).length, facets, hasData: rows.length > 0 };
      }
      const data = await this.catalogue(values, signal);
      await Promise.all(Object.keys(core.catalogueParams).map(async key => {
        const query = Object.fromEntries(core.keys.slice(0, core.keys.indexOf(key)).map(k => [k, values[k]]));
        const own = core.keys.some(k => (query[k] || '') !== (values[k] || '')) ? await this.catalogue(query, signal) : data;
        const filter = own.filters.find(f => f.name === core.catalogueParams[key]);
        const options = (filter?.values || []).filter(v => v.count > 0 && core.text(v.value) && (key !== 'type' || this.selectionOnly || core.catalogueTypeAllowed(values.make, v.value)) && (key !== 'model' || core.catalogueModelAllowed(values.make, v.value))).map(v => ({ value: v.value, label: v.value === 'GM' ? 'Chevrolet / GMC' : v.label, count: v.count }));
        options.sort((a, b) => key === 'year' ? Number(b.value) - Number(a.value) : core.compare(a.label, b.label));
        facets[key] = { options, total: own.count };
      }));
      facets.body = { options: [], total: 0 };
      if (this.selectionOnly) {
        const primary = Object.fromEntries(core.vehicleKeys.map(k => [k, values[k]]));
        const hints = (this.applicationHints || []).map(core.application).filter(app => app && core.matches(app, primary));
        for (const key of ['type', 'body']) {
          for (const app of hints) {
            if (app[key] && !facets[key].options.some(o => core.normal(o.value) === core.normal(app[key]))) facets[key].options.push({ value: app[key], label: app[key], count: 1 });
          }
          facets[key].options.sort((a, b) => core.compare(a.label, b.label));
        }
      }
      const invalidModel = values.make && values.model && !core.catalogueModelAllowed(values.make, values.model);
      const invalidType = !this.selectionOnly && values.make && values.type && !core.catalogueTypeAllowed(values.make, values.type);
      return { count: invalidModel || invalidType ? 0 : data.count, facets, hasData: true };
    }
    populate(key, facet) {
      const field = this.fields[key];
      if (!field) return;
      const current = this.selection[key];
      const choices = facet?.options || [];
      const placeholder = this.selectionOnly && core.vehicleKeys.includes(key) ? 'Select ' + (key === 'engine' ? 'engine size' : key) : blanks[key];
      field.replaceChildren(new Option(placeholder, ''));
      for (const choice of choices) field.add(new Option(choice.label, choice.value));
      const equivalent = choices.find(choice => core.normal(choice.value) === core.normal(current));
      if (current && !equivalent) field.add(new Option(current + ' (no matching choices)', current));
      if (equivalent) this.selection[key] = equivalent.value;
      field.value = this.selection[key];
      field.disabled = !this.canChoose(key) || (!choices.length && !current);
      if (key === 'make') field.toggleAttribute('data-start-here', !this.selection.make && !field.disabled);
    }
    revealFitmentDetails(state) {
      // One available code/body style still needs confirmation. Do not hide it
      // as a redundant filter or silently assume it describes the user's vehicle.
      if (this.mode !== 'applications' && !this.selectionOnly) return;
      const optional = this.querySelector('[data-jsdf-optional]');
      if (!optional) return;
      const ready = complete(this.selection);
      const vehicleKey = ready ? JSON.stringify(['make', 'year', 'model', 'engine'].map(key => this.selection[key])) : '';
      let missing = false;
      for (const key of ['type', 'body']) {
        const row = this.querySelector('.jsdf__' + key + '-row');
        const available = !!state.facets[key]?.options.length || !!this.selection[key];
        row.hidden = !ready || !available;
        if (!row.hidden && !this.selection[key]) missing = true;
      }
      optional.hidden = ['type', 'body'].every(key => this.querySelector('.jsdf__' + key + '-row').hidden);
      if (optional.hidden) optional.open = false;
      else if (missing || vehicleKey !== this.detailsVehicleKey) optional.open = true;
      // Open for a newly completed vehicle even when code/body were restored
      // or populated already. Preserve a deliberate close for the same vehicle.
      this.detailsVehicleKey = optional.hidden ? '' : vehicleKey;
      const summary = optional.querySelector('summary');
      if (summary) summary.textContent = missing
        ? 'Check engine code and body style for fitment'
        : 'Engine code and body style';
    }
    updateProgress(state) {
      const selected = complete(this.selection);
      const label = this.querySelector('[data-jsdf-toggle-label]');
      const vehicleLabel = this.querySelector('[data-jsdf-vehicle-label]');
      if (vehicleLabel) vehicleLabel.hidden = !selected;
      if (label) {
        label.classList.toggle('jsdf__vehicle-details', selected && this.dataset.resultsPage === 'true');
        label.textContent = selected
          ? [this.selection.make, this.selection.year, this.selection.model, this.selection.engine].join(' ')
          : 'Choose your vehicle';
      }
      if (!this.progressive) return;
      const primary = ['make', 'year', 'model', 'engine'];
      for (const key of primary) {
        this.fields[key].required = true;
        this.fields[key].closest('.jsdf__field').hidden = false;
      }
      const optional = this.querySelector('[data-jsdf-optional]');
      if (optional) {
        for (const key of ['type', 'body']) {
          const row = this.querySelector('.jsdf__' + key + '-row');
          const available = state.facets[key]?.options.length > 1 || (!!this.selection[key] && !state.facets[key]?.options.some(x => x.value === this.selection[key]));
          row.hidden = !complete(this.selection) || !available || (key === 'type' && this.dataset.engineType === 'false');
        }
        optional.hidden = ['type', 'body'].every(key => this.querySelector('.jsdf__' + key + '-row').hidden);
        if (!complete(this.selection)) optional.open = false;
      }
      if (this.dataset.resultsPage === 'true') {
        const applied = core.readValues(new URL(location.href), this.mode);
        const results = document.querySelector('js-diesel-results');
        if (results) results.hidden = !complete(applied) || core.keys.some(key => (applied[key] || '') !== (this.selection[key] || ''));
      }
      if (!this.selection.make) this.querySelector('.jsdf__reset').hidden = true;
      else this.querySelector('.jsdf__reset').hidden = false;
    }
    showResultCount() {
      if (this.dataset.resultsPage !== 'true') return;
      const results = document.querySelector('js-diesel-results');
      const applied = core.readValues(new URL(location.href), this.mode);
      if (!complete(applied) || !results?.rows || !results.values ||
          core.keys.some(key => (applied[key] || '') !== (this.selection[key] || '') || (applied[key] || '') !== (results.values[key] || ''))) return;
      const count = core.results(results.rows, applied).length;
      this.message(count + (count === 1 ? ' product matches' : ' products match') + ' your vehicle.');
    }
    async refresh(autoSelect) {
      const sequence = ++this.sequence;
      this.request?.abort();
      this.request = new AbortController();
      const request = this.request;
      const signal = request.signal;
      const timer = setTimeout(() => request.abort(), 20000);
      this.submit.disabled = true;
      Object.values(this.fields).forEach(field => { field.disabled = true; });
      this.retry.hidden = true;
      this.form.setAttribute('aria-busy', 'true');
      this.message('Loading vehicle options…');
      try {
        if (!this.dataset.collectionUrl || !this.dataset.collectionHandle) throw new Error('Missing diesel collection');
        let state;
        for (let pass = 0; pass < core.keys.length; pass++) {
          state = await this.state({ ...this.selection }, signal);
          if (sequence !== this.sequence || !this.isConnected) return;
          if (signal.aborted) throw new Error('Vehicle data timed out');
          let changed = false;
          if (autoSelect && !this.selectionOnly && this.selection.make && state.count > 0) {
            for (const key of core.keys.filter(k => k !== 'make')) {
              if (!this.canChoose(key)) continue;
              if (key === 'type' && this.dataset.engineType === 'false') continue;
              const facet = state.facets[key];
              if (!this.selection[key] && !this.suppressed.has(key) && facet?.options.length === 1 && facet.total > 0 && facet.options[0].count >= facet.total) {
                this.selection[key] = facet.options[0].value;
                changed = true;
                break;
              }
            }
          }
          if (!changed) break;
        }
        for (const key of core.keys) this.populate(key, state.facets[key]);
        this.querySelector('.jsdf__type-row').hidden = (!this.canChoose('type') && !this.selection.type) || (this.dataset.engineType === 'false' && !this.selection.type) || (!state.facets.type.options.length && !this.selection.type);
        this.querySelector('.jsdf__body-row').hidden = (!this.selectionOnly && this.mode !== 'applications') || (!this.canChoose('body') && !this.selection.body) || (!state.facets.body.options.length && !this.selection.body);
        if (!this.progressive) {
          const optional = this.querySelector('[data-jsdf-optional]');
          if (optional) {
            optional.hidden = !this.closest('jss-vehicle-panel') && this.querySelector('.jsdf__type-row').hidden && this.querySelector('.jsdf__body-row').hidden;
            if (optional.hidden) optional.open = false;
          }
        }
        this.updateProgress(state);
        this.revealFitmentDetails(state);
        const applied = core.readValues(new URL(location.href), this.mode);
        const savedNotApplied = this.autoApply && this.selection.make && Object.keys(core.catalogueParams).some(key => (applied[key] || '') !== (this.selection[key] || ''));
        if (this.autoApply) this.submit.hidden = !savedNotApplied;
        this.submit.disabled = this.selectionOnly ? !complete(this.selection) : (!this.selection.make || state.count === 0 || (this.mode === 'applications' && !complete(this.selection)));
        if (this.selectionOnly) this.message(this.submit.disabled ? 'Choose your make, year, model and engine size.' : 'Confirm these are your vehicle details, then check the selected product option.');
        else if (this.progressive && state.hasData) {
          const next = ['make', 'year', 'model', 'engine'].find(key => !this.selection[key]);
          const labels = { make: 'vehicle make', year: 'year', model: 'model', engine: 'engine size' };
          this.message(!state.count
            ? 'No matching vehicle options. Change a choice or reset the finder.'
            : next ? 'Choose your ' + labels[next] + ' to continue.'
            : 'Select Show matching parts to see your results.');
        }
        else if (!state.hasData) this.message('Vehicle search is being updated. Please browse diesel parts below or contact us for fitment help.');
        else if (!this.selection.make) this.message(this.autoApply ? 'Select your vehicle to narrow the products below.' : 'Choose your make, year, model and engine size.');
        else if (!state.count) this.message('0 matching products. Change a choice, reset the finder, or contact us for fitment help.');
        else if (savedNotApplied) this.message('Your saved vehicle is filled in. Select Show matching parts to apply it to this collection.');
        else {
          const next = ['year', 'model', 'engine'].find(key => !this.selection[key] && !this.fields[key].disabled);
          const detail = next === 'engine' ? 'engine size' : next;
          const guidance = next ? ' Choose your ' + detail + ' to continue.' : ' Check each product’s fitment before ordering.';
          this.message(state.count + (state.count === 1 ? ' product matches' : ' products match') + (this.mode === 'applications' ? ' your selected vehicle details.' : ' these filters.') + guidance);
        }
        this.showResultCount();
        return { count: state.count, sequence };
      } catch (error) {
        if (sequence !== this.sequence || !this.isConnected) return;
        this.message('The finder couldn’t load. Try again, browse diesel parts below, or call us.', true);
        this.retry.hidden = false;
      } finally {
        clearTimeout(timer);
        if (sequence === this.sequence) this.form.removeAttribute('aria-busy');
      }
    }
  }
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  function productURL(product, variant) {
    const url = new URL(product.url, location.href);
    if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) throw new Error('Invalid product URL');
    url.search = '';
    if (variant) url.searchParams.set('variant', String(variant.id));
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return window.JSSVehicle ? window.JSSVehicle.withVehicle(url).href : url.href;
  }
  const allowedScopes = ['vehicle', 'universal', 'device_accessory'];
  const categoryHandles = new Set(['diesel-delete-kits-dpf-egr-def-for-duramax-powerstroke-cummins', 'diesel-exhaust-systems', 'delete-pipe', 'tuners', 'cold-air-intake', 'turbo-chargers']);
  function animateCardDetails(details) {
    const summary = details.querySelector('summary');
    let animation = null;
    let expanding = false;
    summary.addEventListener('click', event => {
      event.preventDefault();
      const opening = animation ? !expanding : !details.open;
      const startHeight = details.getBoundingClientRect().height;
      animation?.cancel();
      animation = null;
      details.style.height = '';
      details.style.overflow = '';
      if (!details.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        details.open = opening;
        return;
      }
      details.open = true;
      const endHeight = opening ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height;
      details.style.height = startHeight + 'px';
      details.style.overflow = 'hidden';
      expanding = opening;
      animation = details.animate({ height: [startHeight + 'px', endHeight + 'px'] }, {
        duration: 220,
        easing: 'ease-out'
      });
      const current = animation;
      current.onfinish = () => {
        if (animation !== current) return;
        details.open = opening;
        details.style.height = '';
        details.style.overflow = '';
        animation = null;
        current.cancel();
      };
    });
  }
  function nativeCard(item, scope) {
    const template = document.createElement('template');
    template.innerHTML = item.product.cardHTML || '';
    const card = template.content.querySelector('.product-card-wrapper');
    if (!card || !card.querySelector('[data-fitment-price]') || !card.querySelector('[data-fitment-options]')) throw new Error('Gearx card unavailable');
    card.classList.add('jsdf-native-card');
    card.dataset.productId = item.product.id;
    // Product-level stock/sale/actions are suppressed by Liquid for this opt-in card.
    const variants = [...item.variants].sort((a, b) => Number(a.price) - Number(b.price));
    const area = element('details', 'jsdf-card-details');
    area.append(element('summary', '', scope === 'vehicle' ? 'Matching options & fitment' : 'Product options & details'));
    const body = element('div', 'jsdf-card-details__body');
    area.append(body);
    animateCardDetails(area);
    card.querySelector('[data-fitment-options]').append(area);
    const price = card.querySelector('[data-fitment-price]');
    const comparePrice = element('s', 'price-item price-item--regular jsdf-card-compare');
    price.before(comparePrice);
    const badgeStack = element('div', 'absolute left-10 top-10 jss-card-badge-stack');
    const fitmentBadge = element('jss-card-fitment');
    fitmentBadge.hidden = true;
    const fitmentLabel = element('span', 'jss-card-fitment__label');
    fitmentLabel.setAttribute('data-card-fitment-label', '');
    const fitmentData = element('div');
    fitmentData.hidden = true;
    fitmentData.setAttribute('data-card-fitment-json', '');
    fitmentBadge.append(fitmentLabel, fitmentData);
    const saleBadge = element('div');
    saleBadge.append(element('span', 'tp-badge tp-badge--sale', 'Sale'));
    badgeStack.append(fitmentBadge, saleBadge);
    card.querySelector('.card__inner')?.append(badgeStack);
    const select = element('select', 'jsdf-card-options');
    select.id = 'jsdf-option-' + item.product.id;
    const label = element('label', 'jsdf-card-label', variants.length > 1 ? 'Choose a matching option' : 'Matching option');
    if (scope !== 'vehicle') label.textContent = 'Choose an option';
    label.htmlFor = select.id;
    if (variants.length > 1) select.add(new Option('Select an option', ''));
    variants.forEach(v => select.add(new Option((v.title === 'Default Title' ? 'Standard' : v.title) + ' · ' + v.priceFormatted + (v.available ? '' : ' · Unavailable'), String(v.id))));
    const facts = element('ul', 'jsdf-card-facts');
    const availability = element('li', 'jsdf-card-availability');
    const notes = element('div', 'jsdf-card-notes');
    const action = element('a', 'tp-btn tp-btn__primary jsdf-card-link');
    function update() {
      const chosen = variants.find(v => String(v.id) === select.value);
      const target = chosen || variants[0];
      // Finder choices already exclude nonmatching options. Before a choice,
      // check every displayed option; afterwards check only the selected one.
      const checkedVariants = chosen ? [chosen] : variants;
      fitmentData.textContent = JSON.stringify({
        variantCount: checkedVariants.length,
        variants: checkedVariants.map(v => ({
          id: String(v.id), title: v.title, fitmentClass: v.fitmentClass || '',
          applicationCount: v.applicationCount, applications: v.applications
        }))
      });
      fitmentBadge.dispatchEvent(new Event('jss:card-fitment-update'));
      const url = productURL(item.product, target);
      card.querySelectorAll('.tp__img-url, .card__heading a').forEach(a => { a.href = url; a.removeAttribute('aria-labelledby'); });
      price.replaceChildren();
      if (!chosen && variants.length > 1) price.append(element('span', 'jsdf-card-price-from', 'From '));
      price.append(element('span', 'jsdf-card-price-amount', target.priceFormatted));
      const onSale = Number(target.compareAtPrice) > Number(target.price) && !!target.compareAtPriceFormatted;
      comparePrice.hidden = !onSale;
      comparePrice.textContent = onSale ? target.compareAtPriceFormatted : '';
      price.classList.toggle('jsdf-card-sale-price', onSale);
      saleBadge.hidden = !onSale;
      availability.textContent = chosen ? (chosen.available ? 'Available to order' : 'Currently unavailable to order') : 'Select an option for availability and fitment notes.';
      const unique = [...new Set((chosen?.matchedApplications || []).map(app => String(app.notes || '').trim()).filter(Boolean))];
      notes.replaceChildren();
      notes.hidden = unique.length === 0;
      if (unique.length) {
        notes.append(element('p', 'jsdf-card-notes__heading', 'Fitment notes'));
        unique.forEach(note => notes.append(element('p', '', note)));
      }
      action.href = url;
      action.textContent = chosen ? 'View selected option' : 'View product';
    }
    select.addEventListener('change', update);
    if (variants.length > 1) body.append(label, select);
    else {
      select.value = String(variants[0].id);
      facts.append(element('li', 'jsdf-card-option', variants[0].title === 'Default Title' ? 'One matching option' : variants[0].title));
    }
    if (scope !== 'vehicle') facts.append(element('li', 'jsdf-card-scope', scope === 'universal' ? 'Universal: check dimensions and installation requirements.' : 'Device/accessory: check compatible equipment.'));
    facts.append(availability);
    body.append(facts, notes, action);
    card.querySelectorAll('.tp-loading-image').forEach(wrapper => {
      const img = wrapper.querySelector('img');
      if (img) { img.addEventListener('load', () => wrapper.classList.remove('tp-loading-image'), {once:true}); if (img.complete) wrapper.classList.remove('tp-loading-image'); }
    });
    update();
    return card;
  }
  class DieselResults extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.list = this.querySelector('[data-results-list]');
      this.status = this.querySelector('[data-results-status]');
      this.more = this.querySelector('[data-results-more]');
      this.retry = this.querySelector('[data-results-retry]');
      this.sort = this.querySelector('[data-results-sort]');
      this.category = this.querySelector('[data-results-category]');
      this.scope = this.querySelector('[data-results-scope]');
      const options = { signal: this.events.signal };
      window.addEventListener('jss:vehicle-change', event => {
        this.values = event.detail?.values || emptyValues();
        this.hidden = !complete(core.readValues(new URL(location.href), 'applications')) || !complete(this.values);
        this.updateHeading();
        if (this.rows && this.products) { this.limit = 24; this.render(); }
      }, options);
      const filtersToggle = this.querySelector('[data-results-filters-toggle]');
      filtersToggle?.addEventListener('click', () => {
        const open = filtersToggle.getAttribute('aria-expanded') !== 'true';
        filtersToggle.setAttribute('aria-expanded', String(open));
        filtersToggle.querySelector('[data-filter-symbol]').textContent = open ? '−' : '+';
      }, options);
      this.querySelectorAll('[data-results-view]').forEach(button => button.addEventListener('click', () => {
        this.list.dataset.view = button.dataset.resultsView;
        this.querySelectorAll('[data-results-view]').forEach(control => control.setAttribute('aria-pressed', String(control === button)));
      }, options));
      this.more.addEventListener('click', () => { this.limit += 24; this.render(); }, options);
      for (const field of [this.sort, this.category, this.scope]) field.addEventListener('change', () => { this.limit = 24; this.updateURL(); this.render(); }, options);
      this.retry.addEventListener('click', () => { store.clear(); this.load(); }, options);
      this.querySelector('[data-results-change]').addEventListener('click', () => {
        const finder = document.querySelector('js-diesel-finder[data-results-page="true"]');
        const panel = finder?.querySelector('[data-jsdf-collapse]');
        if (panel) panel.open = true;
        finder?.setVehicleFields(true, false);
        finder?.scrollIntoView?.({behavior:'smooth', block:'start'});
        finder?.querySelector('[data-key="make"]')?.focus({preventScroll:true});
      }, options);
      const clear = this.querySelector('[data-results-clear]');
      clear.href = core.urlFor(this.dataset.collectionUrl, location.href, {}, 'applications').href;
      clear.addEventListener('click', () => { window.JSSVehicle?.clear(); try { sessionStorage.removeItem('jsdf-vehicle-v2'); } catch (_) {} }, options);
      const selected = complete(core.readValues(new URL(location.href), 'applications'));
      this.hidden = !selected || !!document.querySelector('js-diesel-finder[data-results-page="true"] [data-jsdf-collapse]')?.open;
      this.updateHeading();
      // The submitted URL is the boundary for loading product cards and counts.
      if (selected) this.load();
    }
    updateHeading() {
      const selected = complete(core.readValues(new URL(location.href), 'applications'));
      const title = document.querySelector('[data-jsdf-page-title]');
      const intro = document.querySelector('[data-jsdf-page-intro]');
      if (title) title.textContent = selected ? (title.dataset.resultsHeading || 'Parts for your vehicle') : (title.dataset.searchHeading || 'Select your vehicle');
      if (intro) intro.hidden = selected;
    }
    disconnectedCallback() { this.events?.abort(); this.events = null; this.sequence = (this.sequence || 0) + 1; }
    updateURL() {
      const url = new URL(location.href);
      for (const [key, field] of [['jsdf_scope', this.scope], ['jsdf_category', this.category], ['jsdf_sort', this.sort]]) {
        if (field.value) url.searchParams.set(key, field.value); else url.searchParams.delete(key);
      }
      history.replaceState(null, '', url.href);
    }
    async load() {
      const sequence = this.sequence = (this.sequence || 0) + 1;
      this.setAttribute('aria-busy', 'true');
      this.retry.hidden = true;
      this.more.hidden = true;
      this.sort.disabled = this.category.disabled = this.scope.disabled = true;
      this.status.textContent = 'Loading matching parts… Please wait.';
      this.list.replaceChildren();
      try {
        const { rows, products } = await loadApplications(this.dataset.collectionUrl, this.dataset.collectionHandle);
        if (!this.isConnected || sequence !== this.sequence) return;
        this.rows = rows;
        this.products = products;
        const url = new URL(location.href);
        this.values = core.readValues(url, 'applications');
        this.scope.value = allowedScopes.includes(url.searchParams.get('jsdf_scope')) ? url.searchParams.get('jsdf_scope') : 'vehicle';
        this.requestedCategory = url.searchParams.get('jsdf_category') || '';
        if (['title', 'price-ascending', 'price-descending'].includes(url.searchParams.get('jsdf_sort'))) this.sort.value = url.searchParams.get('jsdf_sort');
        this.limit = 24;
        this.sort.disabled = this.category.disabled = this.scope.disabled = false;
        this.render();
      } catch (error) {
        if (!this.isConnected || sequence !== this.sequence) return;
        this.status.textContent = 'Vehicle matches couldn’t load. Please try again or browse diesel parts.';
        this.retry.hidden = false;
      } finally { if (sequence === this.sequence) this.removeAttribute('aria-busy'); }
    }
    render() {
      document.querySelector('js-diesel-finder[data-results-page="true"]')?.showResultCount();
      let items = [];
      const scope = this.scope.value;
      if (scope === 'vehicle') items = complete(this.values) ? core.results(this.rows, this.values) : [];
      else items = this.products.map(product => ({product, variants:product.variants.filter(v => v.fitmentClass === scope)})).filter(item => item.variants.length);
      const selectedCategory = this.requestedCategory || this.category.value;
      this.requestedCategory = '';
      const categories = new Map();
      items.forEach(item => (item.product.categories || []).forEach(category => {
        if (categoryHandles.has(category.handle)) categories.set(category.handle, category.title);
      }));
      this.category.replaceChildren(new Option('All categories', ''));
      [...categories].sort((a,b) => core.compare(a[1],b[1])).forEach(([value,label]) => this.category.add(new Option(label, value)));
      this.category.value = categories.has(selectedCategory) ? selectedCategory : '';
      this.category.disabled = categories.size === 0;
      if (selectedCategory && !categories.has(selectedCategory)) this.updateURL();
      if (this.category.value) items = items.filter(item => (item.product.categories || []).some(c => c.handle === this.category.value));
      const price = item => Math.min(...item.variants.map(variant => Number(variant.price)));
      if (this.sort.value === 'price-ascending') items.sort((a, b) => price(a) - price(b));
      else if (this.sort.value === 'price-descending') items.sort((a, b) => price(b) - price(a));
      else items.sort((a, b) => core.compare(a.product.title, b.product.title));
      const summary = core.keys.map(key => this.values[key]).filter(Boolean).join(' ');
      this.querySelector('[data-results-selection]').hidden = scope === 'vehicle';
      this.querySelector('[data-results-selection]').textContent = scope === 'vehicle' ? (summary || 'Select your vehicle above to find parts.') : (summary ? 'Vehicle retained: ' + summary + '. These products are not confirmed vehicle matches.' : 'Check product compatibility before ordering.');
      const fragment = document.createDocumentFragment();
      try { for (const item of items.slice(0, this.limit)) fragment.append(nativeCard(item, scope)); }
      catch (_) {
        this.list.replaceChildren(); this.more.hidden = true; this.retry.hidden = false;
        this.status.textContent = 'Product cards couldn’t load. Please try again or browse diesel parts.'; return;
      }
      this.list.replaceChildren(fragment);
      this.more.hidden = this.limit >= items.length;
      if (scope === 'vehicle' && !complete(this.values)) this.status.textContent = 'Choose your make, year, model and engine above, then select Find diesel parts.';
      else if (!items.length) this.status.textContent = scope === 'vehicle' ? 'No reviewed applications match these details. Contact us for fitment help or change your selection.' : 'No explicitly classified products are available in this group. Browse all diesel parts or contact us for help.';
      else this.status.textContent = items.length + (items.length === 1 ? ' product' : ' products') + (scope === 'vehicle' ? ' matching your vehicle' : ' in this group') + (items.length > this.limit ? '. Showing ' + this.limit + '.' : '.');
    }
  }
  customElements.define('js-diesel-finder', DieselFinder);
  customElements.define('js-diesel-results', DieselResults);
})();
